import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type PagamentoStatus = 'PENDENTE' | 'CONCILIADO' | 'CONTESTADO';
export type FormaPagamento = 'PIX' | 'TED' | 'DOC' | 'BOLETO' | 'DINHEIRO' | 'CHEQUE';

export interface Pagamento {
  id: string;
  workspaceId: string;
  acordoId: string | null;
  competencia: string;
  credorCpfCnpj: string;
  credorNome: string;
  valor: string;
  dataPagamento: string;
  categoriaDespesa: string | null;
  folhaOrdinaria: string;
  documentoFiscalId: string | null;
  comprovanteR2Key: string | null;
  formaPagamento: FormaPagamento | null;
  status: PagamentoStatus;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface PagamentosListResult {
  items: Pagamento[];
  total: number;
  limit: number;
  offset: number;
}

export interface PagamentosAggregate {
  competencia: string | null;
  totalLancamentos: number;
  totalValor: string;
  pendente: { count: number; total: string };
  conciliado: { count: number; total: string };
  contestado: { count: number; total: string };
}

export interface ListFilters {
  competencia?: string;
  status?: PagamentoStatus;
  search?: string;
  dataInicio?: string;
  dataFim?: string;
  limit?: number;
  offset?: number;
}

export interface CreatePagamentoInput {
  competencia: string;
  credorCpfCnpj: string;
  credorNome: string;
  valor: string;
  dataPagamento: string;
  categoriaDespesa?: string;
  folhaOrdinaria?: boolean;
  documentoFiscalId?: string;
  comprovanteR2Key?: string;
  formaPagamento?: FormaPagamento;
  acordoId?: string;
}

function basePath(workspaceId: string): string {
  return `/v1/workspaces/${workspaceId}/pagamentos`;
}

function buildQuery(filters: ListFilters): string {
  const q = new URLSearchParams();
  if (filters.competencia) q.set('competencia', filters.competencia);
  if (filters.status) q.set('status', filters.status);
  if (filters.search) q.set('search', filters.search);
  if (filters.dataInicio) q.set('dataInicio', filters.dataInicio);
  if (filters.dataFim) q.set('dataFim', filters.dataFim);
  if (filters.limit != null) q.set('limit', String(filters.limit));
  if (filters.offset != null) q.set('offset', String(filters.offset));
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function usePagamentos(workspaceId: string | null | undefined, filters: ListFilters) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['pagamentos', workspaceId, filters],
    queryFn: () =>
      api.get<PagamentosListResult>(`${basePath(workspaceId!)}/${buildQuery(filters)}`),
  });
}

export function usePagamentosAggregate(
  workspaceId: string | null | undefined,
  competencia: string | undefined,
) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['pagamentos', workspaceId, 'aggregate', competencia ?? null],
    queryFn: () =>
      api.get<PagamentosAggregate>(
        `${basePath(workspaceId!)}/aggregate${competencia ? `?competencia=${competencia}` : ''}`,
      ),
  });
}

export function useCreatePagamento(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePagamentoInput) =>
      api.post<Pagamento>(`${basePath(workspaceId!)}/`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pagamentos', workspaceId] });
    },
  });
}

export function useDeletePagamento(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`${basePath(workspaceId!)}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pagamentos', workspaceId] });
    },
  });
}

export function useUpdatePagamentoStatus(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; status: PagamentoStatus }) =>
      api.patch<Pagamento>(`${basePath(workspaceId!)}/${vars.id}/status`, { status: vars.status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pagamentos', workspaceId] });
    },
  });
}

export interface SignedUploadResponse {
  key: string;
  url: string;
  expiresIn: number;
}

export async function signUpload(input: {
  filename: string;
  contentType: string;
  size: number;
  folder: 'comprovantes' | 'notas-fiscais' | 'contratos' | 'documentos';
}): Promise<SignedUploadResponse> {
  return api.post<SignedUploadResponse>('/v1/uploads/sign', input);
}

export type UploadFolder = 'comprovantes' | 'notas-fiscais' | 'contratos' | 'documentos';

export async function uploadFile(file: File, folder: UploadFolder): Promise<string> {
  const signed = await signUpload({
    filename: file.name,
    contentType: file.type,
    size: file.size,
    folder,
  });
  const put = await fetch(signed.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!put.ok) {
    throw new Error(`Upload falhou (${put.status})`);
  }
  return signed.key;
}
